// src/pages/homePage/Hero.tsx
import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import { generateAltText, optimizePexelsUrl } from '../../lib/utils/imageOptimization';
import GradientBorderButton from '../../components/ui/GradientBorderButton';

const Hero = () => {
  const { t } = useLanguage();

  // keep useEffect in case you want to run side-effects later
  useEffect(() => {
    // Example: analytics, scroll effects, etc.
  }, []);

  // fixed background image source
  const heroImage =
    'https://oupvappnlmhqopqsewde.supabase.co/storage/v1/object/public/properties/Margherita.jpg';

  return (
    <div className="relative min-h-screen flex items-center bg-neutral-900 overflow-hidden">
      {/* Hero Background */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.div
            key="image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url("${optimizePexelsUrl(heroImage, 1920, 1080)}")`,
              backgroundAttachment: window.innerWidth > 768 ? 'fixed' : 'scroll',
            }}
            aria-label={generateAltText('hero')}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20"></div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Background Pattern Overlay */}
      <div className="absolute inset-0 z-[5]">
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ opacity: 0.75 }}
        >
          <path
            d="M0,0 L0,100 L30,100 C40,85 48,70 52,50 C56,30 48,15 40,5 C36,2 32,0 28,0 Z"
            fill="var(--hero-pattern-fill)"
            className="transition-all duration-300"
          />
        </svg>
      </div>

      {/* Hero Content */}
      <div className="container-custom relative z-10 py-20 md:py-0 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="md:pr-12 w-full relative z-10"
          >
            <h1
              className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight transition-colors duration-300"
              style={{ color: 'var(--hero-title-color)' }}
            >
              {t.home.hero.title}
            </h1>
            <h2
              className="text-base sm:text-lg lg:text-xl mb-8 leading-relaxed font-normal transition-colors duration-300"
              style={{ color: 'var(--hero-subtitle-color)' }}
              itemProp="description"
            >
              {t.home.hero.subtitle}
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <GradientBorderButton
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => (window.location.href = '/reservations')}
              >
                {t.home.hero.buttons.bookTable}
              </GradientBorderButton>
              <GradientBorderButton
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => (window.location.href = '/menu')}
              >
                {t.home.hero.buttons.exploreMenu}
              </GradientBorderButton>
            </div>
          </motion.div>

          <div className="hidden md:block"></div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
